<template>
  <v-card
    outlined
    class="my-3"
    ref="cardRef"
    :class="{ centeredCard: isFeed, selectedCard: selected, usedCard: item.used }"
  >
    <v-card-subtitle class="mt-0 pb-0">
      <v-row class="align-center">
        <v-col cols="auto" class="py-0">
          <v-checkbox
            :input-value="selected"
            :disabled="item.used"
            hide-details
            class="mt-0 pt-0"
            @change="$emit('toggle-select', item)"
          />
        </v-col>
        <v-col class="py-0">
          <span class="font-weight-semibold">{{ item.site_name }}</span>
          <v-chip x-small class="ml-2" color="primary" outlined>{{ rubricLabel }}</v-chip>
          <v-chip v-if="item.used" x-small class="ml-1" color="grey" outlined>
            déjà envoyé
          </v-chip>
          <span class="ml-2 text-caption">{{ item.category_name }}</span>
        </v-col>
        <v-col cols="auto" class="py-0 text-right text-caption">
          {{ formattedDate }}
        </v-col>
      </v-row>
    </v-card-subtitle>

    <v-card-title class="font-weight-semibold pt-4">
      <a :href="item.url" target="_blank">{{ item.title }}</a>
    </v-card-title>

    <v-card-text class="text-center" v-if="displayImage && !imageError">
      <img
        :src="displayImage"
        :class="{ previewImg: isFeed, smallPreviewImg: isMasonry }"
        @error="imageError = true"
        @load="emitHeight"
      />
    </v-card-text>
    <v-card-text class="text-center py-2" v-else-if="loadingImage">
      <v-progress-circular indeterminate size="20" color="primary" />
    </v-card-text>

    <v-card-text
      class="font-weight-regular text-base description"
      v-if="item.meta_description"
    >
      {{ item.meta_description }}
    </v-card-text>
  </v-card>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";

const RUBRIC_LABELS = {
  cuisine: "Cuisine",
  mode: "Mode",
  beaute: "Beauté",
  maison_deco: "Maison & Déco",
};

export default {
  name: "InternalSourcesCard",
  props: {
    item: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { store } = useProxy();
    const { axiosGet } = useAxios();

    const cardRef = ref(null);
    const imageError = ref(false);
    const resolvedImage = ref(null);
    const loadingImage = ref(false);

    const displayImage = computed(
      () => props.item.image_url || resolvedImage.value
    );

    const emitHeight = () => {
      if (cardRef.value) {
        emit("height-calculated", cardRef.value.$el.offsetHeight);
      }
    };

    const fetchImage = async () => {
      try {
        loadingImage.value = true;
        const {
          data: { image_url },
        } = await axiosGet(`/editorial/leparisien/image/${props.item.id}`, {});
        resolvedImage.value = image_url;
      } catch (error) {
        resolvedImage.value = null;
      }
      loadingImage.value = false;
    };

    const displayMode = computed(() => store.getters["editorial/getDisplayMode"]);
    const isFeed = computed(() => displayMode.value === "feed");
    const isMasonry = computed(() => displayMode.value === "masonry");

    const rubricLabel = computed(
      () => RUBRIC_LABELS[props.item.rubric_slug] || props.item.rubric_slug
    );

    const formattedDate = computed(() => {
      if (!props.item.created) return "";
      const d = new Date(props.item.created);
      if (Number.isNaN(d.getTime())) return props.item.created;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    });

    onMounted(() => {
      setTimeout(emitHeight, 0);
      if (!props.item.image_url) {
        fetchImage();
      }
    });

    return {
      cardRef,
      imageError,
      resolvedImage,
      loadingImage,
      displayImage,
      emitHeight,
      isFeed,
      isMasonry,
      rubricLabel,
      formattedDate,
    };
  },
};
</script>

<style scoped>
.centeredCard {
  width: 40rem;
  margin: auto;
}
.selectedCard {
  border-color: var(--v-primary-base);
  border-width: 2px;
}
.usedCard {
  opacity: 0.55;
}
.previewImg {
  max-width: 30rem;
  max-height: 30rem;
}
.smallPreviewImg {
  max-width: 15rem;
  max-height: 20rem;
}
.description {
  text-align: justify;
}
</style>
